import { useCallback, useEffect, useState } from 'react';
import { isGoogleMapsReady, loadGoogleMaps } from '../../services/helpers/GoogleMapsLoader';

/**
 * Loads the Google Maps JS API on demand (see GoogleMapsLoader) and reports when it is usable.
 * Pass `enabled: false` to hold off until the customer actually uses something that needs it.
 * After a failed load (ad-blocker, key referrer restriction) `loadError` is set; `retry()` clears it
 * and tries again.
 */
export default function useGoogleMaps({ enabled = true }: { enabled?: boolean } = {}) {
  const [isLoaded, setIsLoaded] = useState(isGoogleMapsReady);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || isLoaded || loadError) {
      return;
    }

    let cancelled = false;

    loadGoogleMaps()
      .then(() => !cancelled && setIsLoaded(true))
      .catch((error: Error) => !cancelled && setLoadError(error));

    return () => {
      cancelled = true;
    };
  }, [enabled, isLoaded, loadError, attempt]);

  const retry = useCallback(() => {
    setLoadError(null);
    setAttempt((value) => value + 1);
  }, []);

  return { isLoaded, loadError, retry };
}
